import { assertGradient, type GradientPaint } from "./gradient";
import type { SceneNode } from "../scene/model";
import {
  assertTextDeformation,
  type TextDeformation,
} from "./text-deformation";
export interface CharacterStyle {
  family: string;
  size: number;
  color: string;
  colorAlpha?: number;
  colorGradient?: GradientPaint | null;
  bold: boolean;
  italic: boolean;
  underline: boolean;
}
export interface NativeText extends CharacterStyle {
  deformation?: TextDeformation;
  metrics?: { lineHeight: number; baseline: number; kerning: boolean };
  value: string;
  frame: boolean;
  layoutWidth: number;
  layoutHeight: number;
  align: "left" | "center" | "right";
  lineSpacing: number;
  letterSpacing: number;
  styles: Record<string, Partial<CharacterStyle>>;
  stroke: string;
  strokeAlpha?: number;
  strokeGradient?: GradientPaint | null;
  strokeWidth: number;
}
export const textDefaults: CharacterStyle = {
  family: "3DC Tall Display",
  size: 40,
  color: "#000000",
  bold: false,
  italic: false,
  underline: false,
};
export function textData(node: SceneNode) {
  return node.text as unknown as NativeText;
}
export function characterStyle(t: NativeText, index: number): CharacterStyle {
  const s = t.styles[index];
  const result: CharacterStyle = {
    family: s?.family ?? t.family,
    size: s?.size ?? t.size,
    color: s?.color ?? t.color,
    bold: s?.bold ?? t.bold,
    italic: s?.italic ?? t.italic,
    underline: s?.underline ?? t.underline,
  };
  const gradient =
    s?.colorGradient !== undefined ? s.colorGradient : t.colorGradient;
  if (gradient !== undefined) result.colorGradient = gradient;
  const alpha = s?.colorAlpha ?? t.colorAlpha;
  if (alpha !== undefined) result.colorAlpha = alpha;
  return result;
}
export function textFont(s: CharacterStyle) {
  return `${s.italic ? "italic " : ""}${s.bold ? "bold " : ""}${s.size}px "${s.family.replace(/["\\]/g, "")}"`;
}
export function assertText(node: SceneNode) {
  const t = textData(node);
  assertTextDeformation(t.deformation);
  assertGradient(t.colorGradient);
  assertGradient(t.strokeGradient);
  if (
    (t.metrics !== undefined &&
      (!t.metrics ||
        !Number.isFinite(t.metrics.lineHeight) ||
        t.metrics.lineHeight < 0.5 ||
        t.metrics.lineHeight > 2 ||
        !Number.isFinite(t.metrics.baseline) ||
        t.metrics.baseline < 0 ||
        t.metrics.baseline > 2 ||
        typeof t.metrics.kerning !== "boolean")) ||
    (t.strokeAlpha !== undefined &&
      (!Number.isFinite(t.strokeAlpha) ||
        t.strokeAlpha < 0 ||
        t.strokeAlpha > 1)) ||
    typeof t.value !== "string" ||
    t.value.length > 200000 ||
    typeof t.frame !== "boolean" ||
    !t.styles ||
    typeof t.styles !== "object" ||
    Array.isArray(t.styles) ||
    !/^#[\da-f]{6}$/i.test(t.stroke) ||
    Object.entries(t.styles).some(
      ([key, value]) =>
        !/^\d+$/.test(key) ||
        Number(key) >= t.value.length ||
        !value ||
        typeof value !== "object" ||
        Array.isArray(value),
    ) ||
    ![
      t.layoutWidth,
      t.layoutHeight,
      t.size,
      t.lineSpacing,
      t.letterSpacing,
      t.strokeWidth,
    ].every(Number.isFinite) ||
    t.layoutWidth < 1 ||
    t.layoutHeight < 1 ||
    t.size < 1 ||
    t.size > 1000 ||
    t.lineSpacing < 0.5 ||
    t.lineSpacing > 5 ||
    Math.abs(t.letterSpacing) > 200 ||
    t.strokeWidth < 0 ||
    t.strokeWidth > 10 ||
    !["left", "center", "right"].includes(t.align)
  )
    throw new Error("Повреждён текстовый слой.");
  for (const s of [
    t,
    ...Object.keys(t.styles).map((i) => characterStyle(t, Number(i))),
  ]) {
    assertGradient(s.colorGradient);
    if (
      (s.colorAlpha !== undefined &&
        (!Number.isFinite(s.colorAlpha) ||
          s.colorAlpha < 0 ||
          s.colorAlpha > 1)) ||
      typeof s.family !== "string" ||
      s.family.length > 150 ||
      !/^#[\da-f]{6}$/i.test(s.color) ||
      !Number.isFinite(s.size) ||
      s.size < 1 ||
      s.size > 1000 ||
      [s.bold, s.italic, s.underline].some((v) => typeof v !== "boolean")
    )
      throw new Error("Повреждено оформление текста.");
  }
}
export function createTextNode(
  id: string,
  x: number,
  y: number,
  frame = false,
  width = 240,
  height = 80,
): SceneNode {
  if (!frame) {
    width = 1;
    height = 48;
  }
  return {
    id,
    kind: "text",
    transform: {
      x: x + width / 2,
      y: frame ? y + height / 2 : y - height / 2,
      originX: "center",
      originY: "center",
    },
    geometry: { width, height },
    appearance: { fill: "#000000", opacity: 1 },
    text: {
      ...textDefaults,
      size: frame ? 28 : 40,
      value: "",
      frame,
      layoutWidth: width,
      layoutHeight: height,
      align: "left",
      lineSpacing: 1.2,
      letterSpacing: 0,
      styles: {},
      stroke: "#000000",
      strokeWidth: 0,
    },
    interaction: { role: "decorText" },
    effects: {},
    layer: { name: frame ? "Текстовое поле" : "Текст" },
    media: {},
  };
}
export function styleText(
  node: SceneNode,
  patch: Partial<CharacterStyle>,
  start = 0,
  end = textData(node).value.length,
): SceneNode {
  const t = textData(node),
    styles = { ...t.styles };
  if (start === 0 && end >= t.value.length) {
    for (const key of Object.keys(styles)) {
      const s = { ...styles[key] };
      for (const p of Object.keys(patch)) delete s[p as keyof CharacterStyle];
      styles[key] = s;
    }
    return { ...node, text: { ...t, ...patch, styles } };
  }
  for (let i = start; i < end; i++) styles[i] = { ...styles[i], ...patch };
  return { ...node, text: { ...t, styles } };
}
export function replaceText(
  node: SceneNode,
  value: string,
  typing?: Partial<CharacterStyle>,
): SceneNode {
  const t = textData(node);
  let start = 0;
  while (
    start < value.length &&
    start < t.value.length &&
    value[start] === t.value[start]
  )
    start++;
  let tail = 0;
  while (
    tail < t.value.length - start &&
    tail < value.length - start &&
    t.value[t.value.length - 1 - tail] === value[value.length - 1 - tail]
  )
    tail++;
  const styles: NativeText["styles"] = {};
  for (let i = 0; i < start; i++) if (t.styles[i]) styles[i] = t.styles[i]!;
  const style = typing ?? t.styles[Math.max(0, start - 1)] ?? {};
  for (let i = start; i < value.length - tail; i++) styles[i] = { ...style };
  for (let i = 0; i < tail; i++) {
    const s = t.styles[t.value.length - tail + i];
    if (s) styles[value.length - tail + i] = s;
  }
  return { ...node, text: { ...t, value, styles } };
}
